import { useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { usePageMeta } from "../usePageMeta";
import { StitchSimulator } from "../components/StitchSimulator";

interface ReplayUploadPageProps {
  apiUrl: string;
  token: string;
  shareButtonsEnabled: boolean;
  emailCaptureEnabled: boolean;
  proUpsellVariant: string;
}

// Kept in sync with app/utils/embroidery.py::READ_FORMATS -- the backend is
// the real gate, this is only so the file picker and an obviously wrong file
// fail fast instead of round-tripping to the server first.
const ACCEPTED_EXTENSIONS = [".pes", ".dst", ".exp", ".jef", ".vp3"];

const PAGE_TITLE = "Stitch Simulator — Watch Any Embroidery File Sew | Embroidery Online";
// Kept under ~160 characters -- see the matching note on HomePage.tsx.
const PAGE_DESCRIPTION =
  "Upload a PES, DST, JEF, EXP, or VP3 file and watch it sew stitch by stitch, the way it would on a real machine -- free, no software required.";

// Module-level, not inline in JSX -- see the matching note on HomePage.tsx.
// MultimediaApplication rather than DesignApplication: this page plays back
// an existing design, it doesn't create one.
const STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Embroidery Stitch Simulator",
    url: "https://embroiderytext.com/replay",
    description: PAGE_DESCRIPTION,
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

/**
 * The direct entry point for the stitch simulator: upload a file, watch it
 * sew right here, next to the upload form -- no navigating away. The other
 * view of the simulator, ReplayPage (/replay/{id} and /replay/content/{key}),
 * is reached instead from a "Watch it stitch" result on /create or /convert,
 * where the design already exists as its own shareable page.
 */
export function ReplayUploadPage({
  apiUrl,
  token,
  shareButtonsEnabled,
  emailCaptureEnabled,
  proUpsellVariant,
}: ReplayUploadPageProps) {
  const [file, setFile] = useState<File | null>(null);
  const [contentKey, setContentKey] = useState<string | null>(null);
  // Set alongside contentKey from the same /api/convert response, only when
  // a token was sent -- see StitchSimulator's emailAssetId prop for why this
  // is kept separate from contentKey rather than replacing it.
  const [assetId, setAssetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const posthog = usePostHog();

  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/replay", structuredData: STRUCTURED_DATA });

  const extensionOf = (name: string) => {
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
  };

  const handleFileChange = (selected: File | null) => {
    setError("");
    setContentKey(null);
    setAssetId(null);
    if (selected && !ACCEPTED_EXTENSIONS.includes(extensionOf(selected.name))) {
      setFile(null);
      setError(
        `Unsupported file type. Supported formats: ${ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join(", ").toUpperCase()}`
      );
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Choose a file to simulate");
      return;
    }

    setLoading(true);
    setError("");
    setContentKey(null);
    setAssetId(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (token) formData.append("token", token);

      const response = await fetch(`${apiUrl}/api/convert`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || "Could not read this file");
      }

      const data = await response.json();
      posthog?.capture("stitch_replay_upload", { source_format: data.source_format });
      // StitchSimulator takes it from here -- it fetches the point cloud for
      // this same content_key itself, the same way it does for the
      // /replay/content/{key} page. asset_id is separate: only used to let
      // its own downloads be emailed (see its emailAssetId prop).
      setContentKey(data.content_key);
      setAssetId(data.asset_id);
    } catch (err: any) {
      setError(err.message || "Could not read this file");
    } finally {
      setLoading(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setContentKey(null);
    setAssetId(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <section className="tool-intro">
        <h1>Stitch Simulator</h1>
        <p>
          Upload a design and watch it sew, stitch by stitch, the way it
          would on a real machine -- no software, nothing to install.
        </p>
      </section>

      <div className="two-column-layout">
        <div className="column-left">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <label>
              Embroidery file
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                required
              />
            </label>
            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              Supported formats: {ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join(", ").toUpperCase()}
            </small>

            {error && (
              <article className="error-message" style={{ marginTop: "1rem" }}>
                <p><strong>Error:</strong> {error}</p>
              </article>
            )}

            <button
              type="submit"
              aria-busy={loading}
              disabled={loading || !file}
              style={{ width: "100%", marginTop: "1.25rem" }}
            >
              {loading ? "Reading your file…" : "▶ Simulate Stitching"}
            </button>

            {file && (
              <button
                type="button"
                className="secondary outline"
                onClick={resetFile}
                disabled={loading}
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="column-right">
          <h2 className="column-heading">Simulator</h2>

          {!loading && !contentKey && !error && (
            <div className="empty-preview">
              <p>Your stitch simulation will appear here</p>
              <small>Upload a file on the left to watch it stitch</small>
            </div>
          )}

          {loading && (
            <div className="empty-preview">
              <p aria-busy="true">Reading your file…</p>
            </div>
          )}

          {!loading && contentKey && (
            <StitchSimulator
              apiUrl={apiUrl}
              contentKey={contentKey}
              showDownloads={shareButtonsEnabled}
              token={token}
              emailCaptureEnabled={emailCaptureEnabled}
              proUpsellVariant={proUpsellVariant}
              emailAssetId={assetId ?? undefined}
              emailSource="replay_upload"
            />
          )}
        </div>
      </div>

      <section className="tool-details">
        <h2>Preview any embroidery file before it hits the hoop</h2>
        <p>
          See exactly how a design will sew before you commit thread to
          fabric -- catch an unexpectedly dense area, a strange color order,
          or more trims than you expected, all without touching a machine.
          Works with files from Ink/Stitch, Hatch, Wilcom, and most other
          digitizing software.
        </p>
      </section>
    </>
  );
}
